import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { Label } from './ui/label';
import { Globe, Volume2, Check } from 'lucide-react';
import { languages } from '../mockData';
import { toast } from '../hooks/use-toast';
import { userAPI } from '../services/api';

const LanguageSelection = () => {
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [voiceGender, setVoiceGender] = useState('female');
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!selectedLanguage) {
      toast({
        title: 'Please select a language',
        description: 'Choose which language you want to learn',
        variant: 'destructive',
      });
      return;
    }
    setLoading(true);
    try {
      await userAPI.updateLanguage(selectedLanguage, voiceGender);
      localStorage.setItem('selectedLanguage', selectedLanguage);
      localStorage.setItem('voiceGender', voiceGender);
      toast({
        title: 'Language selected!',
        description: `You're ready to start learning ${languages.find(l => l.id === selectedLanguage)?.name}`,
      });
      navigate('/dashboard');
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save language preference',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-sky-50">
      <nav className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2">
              <Globe className="h-8 w-8 text-amber-600" />
              <span className="text-xl font-bold text-slate-900">NomadLinguist</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">
            Choose Your Language
          </h1>
          <p className="text-xl text-slate-600">
            Select the language you want to learn and your preferred voice
          </p>
        </div>

        {/* Foreign Languages */}
        <Card className="border-2 mb-6">
          <CardHeader>
            <CardTitle>Foreign Languages</CardTitle>
            <CardDescription>European languages for international travel</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {languages.filter(lang => ['spanish', 'french', 'german', 'italian'].includes(lang.id)).map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`p-4 rounded-lg border-2 text-left hover:border-amber-400 hover:shadow-md transition-all ${
                    selectedLanguage === lang.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-3xl mb-2">{lang.flag}</p>
                      <p className="font-semibold text-slate-900">{lang.name}</p>
                      <p className="text-sm text-slate-600">{lang.nativeName}</p>
                    </div>
                    {selectedLanguage === lang.id && (
                      <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Indian Languages */}
        <Card className="border-2 mb-8">
          <CardHeader>
            <CardTitle>Indian Languages</CardTitle>
            <CardDescription>Regional languages for traveling across India</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {languages.filter(lang => ['hindi', 'tamil', 'kannada', 'telugu', 'malayalam'].includes(lang.id)).map((lang) => (
                <button
                  key={lang.id}
                  onClick={() => setSelectedLanguage(lang.id)}
                  className={`p-4 rounded-lg border-2 text-left hover:border-amber-400 hover:shadow-md transition-all ${
                    selectedLanguage === lang.id
                      ? 'border-amber-500 bg-amber-50'
                      : 'border-slate-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-3xl mb-2">{lang.flag}</p>
                      <p className="font-semibold text-slate-900">{lang.name}</p>
                      <p className="text-sm text-slate-600">{lang.nativeName}</p>
                    </div>
                    {selectedLanguage === lang.id && (
                      <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center">
                        <Check className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 className="h-5 w-5" />
              Voice Preference
            </CardTitle>
            <CardDescription>Choose male or female voice for pronunciations</CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup value={voiceGender} onValueChange={setVoiceGender}>
              <div className="flex items-center space-x-8">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="female" id="female" />
                  <Label htmlFor="female" className="cursor-pointer">Female Voice</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="male" id="male" />
                  <Label htmlFor="male" className="cursor-pointer">Male Voice</Label>
                </div>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        <div className="flex justify-center">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-700 px-12"
            onClick={handleContinue}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Start Learning'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LanguageSelection;